import { useCallback, useEffect, useRef } from 'preact/hooks'
import { batch } from '@preact/signals'
import { reportChatFailure } from '@/monitoring/chatDiagnostics'
import { useCompany } from './useCompany'
import { useToast } from './useToast'
import {
  startConversation,
  sendMessage,
  applyToField as applyToFieldAPI,
  completeConversation,
  updateFeedback,
  updateMessageFeedback,
} from '@/api/aiChat'
import type {
  ChatMessage,
  AIConversationContext,
  AIConversationSessionDto,
  AIAction,
  ContextBuilder,
} from '@/types/aiChat'
import { buildActivitySteps, turnStartIndex } from '@/utils/activitySteps'
import { hasChatContextChanged } from '@/utils/chatContext'
import { ChatEntityTypes, ChatLocations, ChatViewModes } from '@/constants/aiChat'
import {
  aiChatState,
  fieldUpdateHandler,
  initialSessionContext,
  lastClosedSession,
} from '@/state/aiChat'
import { useUserProfile, USER_ROLES } from './useUserProfile'
import type { UserCompanyRoleSetViewDto } from '@/types/user'
import type { QuizDraftDescription } from '@/utils/quizDraft'

// --- Helpers ---

const generateMessageId = () =>
  `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`

/** Session-level spend, accumulated server-side across every model call in the session. */
const getSpendFields = (session: AIConversationSessionDto) => ({
  totalCostUsd: session.TotalCostUsd ?? 0,
  totalInputTokens: session.TotalInputTokens ?? 0,
  totalOutputTokens: session.TotalOutputTokens ?? 0,
})

/** Strip markdown bold/italic and surrounding quotes */
function cleanMarkdown(value: string): string {
  return value
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/^["']|["']$/g, '')
    .trim()
}

/**
 * Whether the user can edit the given workspace — mirrors the API's IsWorkspaceEditor
 * hierarchy (company Admin/Editor OR workspace Admin/Editor). Used to keep the Webster
 * chat read-only for Viewers (ORB-434).
 *
 * Kept as its own helper rather than calling useUserProfile().getWorkspaceCapabilities, which now
 * folds company Editor too and gives the same answer. The two encode one rule and must stay in step:
 * change either and change both.
 */
function canEditWorkspace(
  roleSets: UserCompanyRoleSetViewDto[] | null | undefined,
  clientCompanyId: string,
  workspaceId: string
): boolean {
  const roleSet = roleSets?.find(rs => rs.ClientCompanyId === clientCompanyId)
  if (!roleSet) return false

  if (
    roleSet.IsAdmin ||
    roleSet.CompanyRoles.includes(USER_ROLES.ADMIN) ||
    roleSet.CompanyRoles.includes(USER_ROLES.EDITOR)
  ) {
    return true
  }

  const workspaceRoles = roleSet.WorkspaceRoles.find(wr => wr.WorkspaceId === workspaceId)
  return (
    !!workspaceRoles &&
    (workspaceRoles.Roles.includes(USER_ROLES.ADMIN) || workspaceRoles.Roles.includes(USER_ROLES.EDITOR))
  )
}

/** Build a ChatMessage from the latest assistant message in a session response,
 *  tracking its raw index in the backend Messages array for feedback, and carrying the
 *  turn's tool calls so they can be rendered as an activity stream. */
function extractLatestAssistantMessage(session: AIConversationSessionDto): ChatMessage | null {
  // Find the last assistant message by scanning the full array in reverse
  // so we capture the raw backend index, not a filtered index.
  let lastIdx = -1
  for (let i = session.Messages.length - 1; i >= 0; i--) {
    if (session.Messages[i].Role === 'assistant') { lastIdx = i; break }
  }
  if (lastIdx === -1) return null
  const latest = session.Messages[lastIdx]

  const steps = buildActivitySteps(session.Messages, turnStartIndex(session.Messages))

  return {
    id: generateMessageId(),
    role: 'assistant',
    content: latest.Content,
    timestamp: new Date(latest.Timestamp),
    generatedVariations: latest.GeneratedVariations ?? undefined,
    targetFieldName: latest.TargetFieldName,
    backendIndex: lastIdx,
    steps: steps.length > 0 ? steps : undefined,
    quizDraft: latest.QuizDraft ?? undefined,
  }
}

/** Apply an assistant response from the API to chat state.
 *  Also backfills backend indices for any local messages that don't have one yet
 *  (e.g. the user message we just sent). */
function applySessionResponse(session: AIConversationSessionDto) {
  const assistantMessage = extractLatestAssistantMessage(session)
  const spend = getSpendFields(session)

  // Backfill backendIndex for the most recent user message if missing.
  // The last 'user' message in the backend array is the one we just sent.
  let messages = aiChatState.value.messages
  const lastLocal = messages[messages.length - 1]
  if (lastLocal && lastLocal.role === 'user' && lastLocal.backendIndex == null) {
    let lastUserIdx = -1
    for (let i = session.Messages.length - 1; i >= 0; i--) {
      if (session.Messages[i].Role === 'user') { lastUserIdx = i; break }
    }
    if (lastUserIdx >= 0) {
      messages = messages.map((m, i) =>
        i === messages.length - 1 ? { ...m, backendIndex: lastUserIdx } : m
      )
    }
  }

  if (assistantMessage) {
    aiChatState.value = {
      ...aiChatState.value,
      messages: [...messages, assistantMessage],
      isLoading: false,
      ...spend,
    }
  } else {
    aiChatState.value = {
      ...aiChatState.value,
      messages,
      isLoading: false,
      ...spend,
    }
  }
}

// --- Feedback sub-hook ---

function useAIChatFeedback(
  companyId: string | null | undefined,
  currentWorkspace: { Id: string } | null | undefined,
) {
  const provideFeedback = useCallback(
    async (rating: boolean) => {
      const sessionId = aiChatState.value.sessionId || lastClosedSession.value

      if (!sessionId || !companyId || !currentWorkspace) {
        console.warn('No active or recent session for feedback')
        return
      }

      try {
        batch(() => {
          aiChatState.value = { ...aiChatState.value, rating, showDelayedFeedback: false }
          lastClosedSession.value = null
        })
        await updateFeedback(companyId, currentWorkspace.Id, sessionId, rating)
      } catch (error) {
        console.error('Error providing feedback:', error)
      }
    },
    [companyId, currentWorkspace]
  )

  const provideMessageFeedback = useCallback(
    async (messageId: string, rating: boolean, comment?: string) => {
      const state = aiChatState.value
      if (!state.sessionId || !companyId || !currentWorkspace) return

      const message = state.messages.find(m => m.id === messageId)
      if (!message || message.backendIndex == null) return

      aiChatState.value = {
        ...state,
        messages: state.messages.map(m =>
          m.id === messageId ? { ...m, rating, feedbackComment: comment } : m
        ),
      }

      try {
        await updateMessageFeedback(
          companyId,
          currentWorkspace.Id,
          state.sessionId,
          message.backendIndex,
          rating,
          comment
        )
      } catch (error) {
        console.error('Error providing message feedback:', error)
      }
    },
    [companyId, currentWorkspace]
  )

  const hideDelayedFeedback = useCallback(() => {
    aiChatState.value = { ...aiChatState.value, showDelayedFeedback: false }
  }, [])

  return { provideFeedback, provideMessageFeedback, hideDelayedFeedback }
}

// --- Main hook ---

/**
 * Hook for reading AI chat state and calling actions
 * Components that use this will re-render when state changes
 */
export function useAIChat() {
  // Reading .value during render subscribes the calling component to changes
  const state = aiChatState.value
  const { companyId, currentWorkspace } = useCompany()
  const { userCompanyRoleSets } = useUserProfile()
  const { showError, showSuccess } = useToast()

  // --- Domain isolation: hard-reset chat when company or workspace changes ---
  const prevCompanyRef = useRef(companyId)
  const prevWorkspaceRef = useRef(currentWorkspace?.Id)

  useEffect(() => {
    const prevCompany = prevCompanyRef.current
    const prevWorkspace = prevWorkspaceRef.current
    const currWorkspaceId = currentWorkspace?.Id

    // Skip on initial mount (no previous values to compare)
    if (prevCompany == null && prevWorkspace == null) {
      prevCompanyRef.current = companyId
      prevWorkspaceRef.current = currWorkspaceId
      return
    }

    const companyChanged = prevCompany != null && companyId !== prevCompany
    const workspaceChanged = prevWorkspace != null && currWorkspaceId !== prevWorkspace

    // Always update refs to current values so we don't fire twice
    // (e.g. company changes on one render, then workspace follows on the next)
    prevCompanyRef.current = companyId
    prevWorkspaceRef.current = currWorkspaceId

    if (companyChanged || workspaceChanged) {
      // Hard reset — don't call completeConversation since the old session
      // belongs to a different company/workspace context
      batch(() => {
        aiChatState.value = {
          isOpen: false,
          sessionId: null,
          messages: [],
          context: null,
          isLoading: false,
          error: null,
          isMinimized: false,
          rating: null,
          showDelayedFeedback: false,
          totalCostUsd: 0,
          totalInputTokens: 0,
          totalOutputTokens: 0,
        }
        lastClosedSession.value = null
        initialSessionContext.value = null
        fieldUpdateHandler.value = null
      })

      // After a company change the workspace hasn't updated yet on this render —
      // null out the ref so the follow-up workspace update doesn't trigger a second reset
      if (companyChanged) {
        prevWorkspaceRef.current = undefined
      }
    }
  }, [companyId, currentWorkspace?.Id])

  const {
    provideFeedback,
    provideMessageFeedback,
    hideDelayedFeedback,
  } = useAIChatFeedback(companyId, currentWorkspace)

  const openChat = useCallback(
    async (contextBuilder?: ContextBuilder) => {
      if (!companyId || !currentWorkspace) {
        showError('Error', 'Workspace not available')
        return
      }

      const currentState = aiChatState.value
      if (currentState.isOpen) return

      const builtContext = contextBuilder
        ? contextBuilder()
        : (currentState.context || {
            location: ChatLocations.Other,
            entityType: ChatEntityTypes.None,
            viewMode: ChatViewModes.View,
            availableFields: [],
            availableActions: ['CopyToClipboard'],
          })

      // A Viewer (no edit capability in this workspace) is read-only: force View mode
      // so Webster never offers apply/edit affordances and the session matches what the
      // API enforces (ORB-434). We check currentWorkspace.Id — the workspace that
      // startConversation() authorizes against (the route) — which since ORB-473 is the
      // only workspace in play: the context no longer carries one of its own.
      const context = canEditWorkspace(userCompanyRoleSets, companyId, currentWorkspace.Id)
        ? builtContext
        : { ...builtContext, viewMode: ChatViewModes.View }

      aiChatState.value = {
        ...currentState,
        isOpen: true,
        isLoading: true,
        context,
        error: null,
        rating: null,
        showDelayedFeedback: false,
      }

      try {
        const session = await startConversation(companyId!, currentWorkspace.Id, context)
        initialSessionContext.value = context

        // Find first assistant message with its raw backend index
        const firstAssistantIdx = session.Messages.findIndex(m => m.Role === 'assistant')
        const initialMessage: ChatMessage = firstAssistantIdx >= 0
          ? {
              id: generateMessageId(),
              role: 'assistant',
              content: session.Messages[firstAssistantIdx].Content,
              timestamp: new Date(session.Messages[firstAssistantIdx].Timestamp),
              generatedVariations: session.Messages[firstAssistantIdx].GeneratedVariations ?? undefined,
              targetFieldName: session.Messages[firstAssistantIdx].TargetFieldName,
              backendIndex: firstAssistantIdx,
            }
          : {
              id: generateMessageId(),
              role: 'assistant',
              content: 'Hello! How can I help you today?',
              timestamp: new Date(),
            }

        aiChatState.value = {
          ...aiChatState.value,
          sessionId: session.Id,
          messages: [initialMessage],
          isLoading: false,
          rating: null,
          ...getSpendFields(session),
        }
      } catch (error) {
        console.error('Error opening chat:', error)
        aiChatState.value = {
          ...aiChatState.value,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to start conversation',
        }
        showError('AI Chat Error', 'Failed to start conversation')
      }
    },
    [companyId, currentWorkspace, showError, userCompanyRoleSets]
  )

  const closeChat = useCallback(() => {
    const currentState = aiChatState.value
    const currentContext = currentState.context
    const shouldShowDelayedFeedback = currentState.rating === null || currentState.rating === undefined
    const closingSessionId = currentState.sessionId

    batch(() => {
      aiChatState.value = {
        isOpen: false,
        sessionId: null,
        messages: [],
        context: currentContext,
        isLoading: false,
        error: null,
        isMinimized: false,
        rating: null,
        showDelayedFeedback: shouldShowDelayedFeedback,
        totalCostUsd: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
      }

      if (shouldShowDelayedFeedback && closingSessionId) {
        lastClosedSession.value = closingSessionId
      }

      initialSessionContext.value = null
    })

    if (closingSessionId && companyId && currentWorkspace) {
      completeConversation(companyId, currentWorkspace.Id, closingSessionId, 'Dismissed', undefined, undefined, currentState.rating)
        .catch(error => console.error('Error completing conversation:', error))
    }
  }, [companyId, currentWorkspace])

  const toggleChat = useCallback(
    (contextBuilder?: ContextBuilder) => {
      if (aiChatState.value.isOpen) {
        closeChat()
      } else {
        openChat(contextBuilder)
      }
    },
    [openChat, closeChat]
  )

  const sendUserMessage = useCallback(
    async (message: string) => {
      const currentState = aiChatState.value
      if (!currentState.sessionId || !currentState.context || !companyId || !currentWorkspace) {
        showError('Error', 'No active conversation')
        return
      }

      const userMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content: message,
        timestamp: new Date(),
      }

      aiChatState.value = {
        ...currentState,
        messages: [...currentState.messages, userMessage],
        isLoading: true,
      }

      const startedAt = performance.now()
      let responseReceived = false
      try {
        const currentContext = aiChatState.value.context
        const savedInitialContext = initialSessionContext.value
        // True only when both contexts exist, so the narrowing below is safe: a change cannot
        // be reported against a context that is not there.
        const hasContextChanged = hasChatContextChanged(savedInitialContext, currentContext)
        const updatedContext = hasContextChanged && currentContext ? currentContext : undefined

        const session = await sendMessage(
          companyId!,
          currentWorkspace.Id,
          currentState.sessionId!,
          message,
          updatedContext
        )
        responseReceived = true

        if (updatedContext) {
          initialSessionContext.value = updatedContext
        }

        applySessionResponse(session)
      } catch (error) {
        // The turn-limit branch that used to live here sniffed the 422's message text for
        // "maximum" and "message". The cap it detected is gone (ORB-763), so the branch was
        // unreachable — and string-matching an error message was never a sound way to find it.
        console.error('Error sending message:', error)
        // Request failures are reported at the shared API boundary. This branch isolates
        // a client failure after the server has already returned a successful turn.
        if (responseReceived) {
          reportChatFailure('apply-response', {
            sessionId: currentState.sessionId, elapsedMs: Math.round(performance.now() - startedAt),
          })
        }
        aiChatState.value = { ...aiChatState.value, isLoading: false }
        showError('AI Chat Error', 'Failed to send message')
      }
    },
    [companyId, currentWorkspace, showError]
  )

  const applyToField = useCallback(
    async (
      fieldName: string,
      value: string,
      variationIndex?: number,
      messageIndex?: number,
      options?: { skipMarkdownCleanup?: boolean },
    ): Promise<boolean> => {
      if (!companyId || !currentWorkspace) {
        showError('Error', 'No workspace selected')
        return false
      }

      // True once the field handler has taken the value, whether or not the server then records it.
      let applied = false

      try {
        // A quiz draft is JSON, not a model option: cleaning it would delete asterisks inside its text.
        const cleanValue = options?.skipMarkdownCleanup ? value : cleanMarkdown(value)
        const handler = fieldUpdateHandler.value

        // 1. Apply value to form field immediately (local-first)
        if (!handler) {
          return false
        }
        try {
          await handler(fieldName, cleanValue)
        } catch (handlerError) {
          console.error('Field update handler error:', handlerError)
          showError('Error', 'Failed to update field value')
          return false
        }
        applied = true

        // 2. Show success feedback
        const currentState = aiChatState.value
        const field = currentState.context?.availableFields?.find(f => f.name === fieldName)
        const displayName = field?.displayName || fieldName
        showSuccess('Applied', `Value applied to ${displayName}`)

        // 3. Record to backend and get AI response
        if (currentState.sessionId) {
          aiChatState.value = { ...aiChatState.value, isLoading: true }

          try {
            const session = await applyToFieldAPI(
              companyId!,
              currentWorkspace.Id,
              currentState.sessionId,
              fieldName,
              cleanValue,
              variationIndex,
              messageIndex
            )

            applySessionResponse(session)
          } catch (error) {
            console.warn('Failed to record AI action:', error)
            aiChatState.value = { ...aiChatState.value, isLoading: false }
          }
        }
      } catch (error) {
        console.error('Error applying value:', error)
        showError('Error', 'Failed to apply value')
      }
      return applied
    },
    [companyId, currentWorkspace, showError, showSuccess]
  )

  // Kept on the message, not in the card, because the card is remounted whenever the chat is minimized and restored.
  const markQuizDraftApplied = useCallback((messageId: string, description: QuizDraftDescription) => {
    aiChatState.value = {
      ...aiChatState.value,
      messages: aiChatState.value.messages.map(message =>
        message.id === messageId ? { ...message, quizDraftApplied: description } : message
      ),
    }
  }, [])

  const executeAction = useCallback(
    async (action: AIAction) => {
      switch (action.type) {
        case 'ApplyToField':
          if (action.targetField && action.value !== undefined) {
            await applyToField(action.targetField, action.value, action.variationIndex)
          }
          break

        case 'CopyToClipboard':
          if (action.value) {
            await navigator.clipboard.writeText(action.value)
            showSuccess('Copied', 'Content copied to clipboard')
          }
          break

        case 'RegenerateVariations':
          await sendUserMessage('Generate more variations')
          break

        default:
          console.warn('Unknown action type:', action.type)
      }
    },
    [applyToField, sendUserMessage, showSuccess]
  )

  const minimizeChat = useCallback(() => {
    aiChatState.value = { ...aiChatState.value, isMinimized: true }
  }, [])

  const maximizeChat = useCallback(() => {
    aiChatState.value = { ...aiChatState.value, isMinimized: false }
  }, [])

  const setFieldUpdateHandler = useCallback(
    (handler: typeof fieldUpdateHandler.value) => {
      fieldUpdateHandler.value = handler
    },
    []
  )

  return {
    state,
    openChat,
    closeChat,
    toggleChat,
    sendUserMessage,
    applyToField,
    markQuizDraftApplied,
    executeAction,
    minimizeChat,
    maximizeChat,
    setFieldUpdateHandler,
    provideFeedback,
    provideMessageFeedback,
    hideDelayedFeedback,
  }
}

/**
 * Hook for only updating AI chat context (doesn't subscribe to state changes)
 * Use this in components that only need to update context, not read it
 */
export function useAIChatContextUpdater() {
  const { companyId, currentWorkspace } = useCompany()
  const { userCompanyRoleSets } = useUserProfile()
  const updateContext = useCallback(
    (newContext: AIConversationContext) => {
      // Keep a Viewer's context read-only even when a page updates it mid-session
      // (e.g. navigating into an editor view). Mirrors the openChat clamp (ORB-434).
      // Capability is checked against currentWorkspace.Id — the workspace in the API
      // route, and so the one the server authorizes against (ORB-473).
      const context = companyId && currentWorkspace
        && canEditWorkspace(userCompanyRoleSets, companyId, currentWorkspace.Id)
        ? newContext
        : { ...newContext, viewMode: ChatViewModes.View }
      // peek, never .value: this runs from useAIForm's effect, which Preact can flush inside a render, and a
      // .value read there subscribes that component to the state it is about to write. It then re-rendered
      // on its own write and looped until the page froze (ORB-571).
      const current = aiChatState.peek()
      // Callers push on every render; an unchanged context is not worth waking every subscriber for.
      if (current.context && JSON.stringify(current.context) === JSON.stringify(context)) return
      aiChatState.value = { ...current, context }
    },
    [companyId, currentWorkspace, userCompanyRoleSets]
  )

  const setFieldUpdateHandler = useCallback(
    (handler: typeof fieldUpdateHandler.value) => {
      fieldUpdateHandler.value = handler
    },
    []
  )

  return {
    updateContext,
    setFieldUpdateHandler,
  }
}
